import { View } from "@react-pdf/renderer";
import {
  ResumePDFIcon,
  type IconType,
} from "components/Resume/ResumePDF/common/ResumePDFIcon";
import { styles, spacing } from "components/Resume/ResumePDF/styles";

import type { ResumeProfile } from "lib/redux/types";
import { extractUsernameFromURL } from "lib/utils/urlHelpers";

import { ResumePDFLink, ResumePDFText } from "../ResumePDF/common";
import { TemplateSeventeenProfileSection } from "./common";
import { useFontSize } from "components/Resume/ResumePDF/FontSizeContext";

const Profile = ({
  profile,
  themeColor,
  isPDF,
  enableHighlight = false,
}: {
  profile: ResumeProfile;
  themeColor: string;
  isPDF: boolean;
  enableHighlight?: boolean;
}) => {
  const { fontSize } = useFontSize();
  const numericFontSize = Number(fontSize);
  const { name, email, phone, url1, url2, summary, location } = profile;
  const iconProps = { location, email, phone, url1, url2 };

  return (
    <TemplateSeventeenProfileSection style={{ marginTop: spacing["4"] }}>
      <ResumePDFText
        bold={true}
        themeColor={themeColor}
        style={{
          fontSize: "35pt",
          textAlign: "center",
        }}
      >
        {name}
      </ResumePDFText>
      <View
        style={{
          ...styles.flexRow,
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 15,
          rowGap: 5,
          paddingTop: spacing["1"],
          paddingBottom: spacing["1"],
          marginTop: spacing["0.5"],
          marginBottom: "1pt",
          borderBottom: `1pt solid ${themeColor}`,
          borderTop: `1pt solid ${themeColor}`,
        }}
      >
        {Object.entries(iconProps).map(([key, value], index) => {
          if (!value) return null;

          let iconType = key as IconType;
          let displayValue = value;
          if (key === "url1" || key === "url2") {
            if (value.includes("github")) {
              iconType = "url_github";
            } else if (value.includes("linkedin")) {
              iconType = "url_linkedin";
            } else if (value.includes("behance")) {
              iconType = "url_behance";
            } else if (value.includes("dribble")) {
              iconType = "url_dribble";
            }
            displayValue = extractUsernameFromURL(value);
          }

          const shouldUseLinkWrapper = [
            "email",
            "url1",
            "url2",
            "phone",
          ].includes(key);
          const Wrapper = ({ children }: { children: React.ReactNode }) => {
            if (!shouldUseLinkWrapper) return <>{children}</>;

            let src = "";
            switch (key) {
              case "email": {
                src = `mailto:${value}`;
                break;
              }
              case "phone": {
                src = `tel:${value.replace(/[^\d+]/g, "")}`; // Keep only + and digits
                break;
              }
              default: {
                src = value.startsWith("http") ? value : `https://${value}`;
              }
            }

            return (
              <ResumePDFLink src={src} isPDF={isPDF}>
                {children}
              </ResumePDFLink>
            );
          };

          return (
            <View
              key={key}
              style={{
                ...styles.flexRow,
                alignItems: "center",
                justifyContent: "center",
                gap: spacing["1"],
              }}
            >
              <ResumePDFIcon
                type={iconType}
                isPDF={isPDF}
                themeColor={themeColor}
              />
              <Wrapper>
                <ResumePDFText style={{ fontSize: `${numericFontSize * 0.875}pt` }}>
                  {displayValue}
                </ResumePDFText>
              </Wrapper>

              {index < Object.entries(iconProps).length - 1 && (
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    marginLeft: spacing["2"],
                    backgroundColor: themeColor,
                    marginHorizontal: 10,
                  }}
                />
              )}
            </View>
          );
        })}
      </View>
    </TemplateSeventeenProfileSection>
  );
};

export default Profile;
